import React, { useState } from 'react';
import { useLocale } from '@platform/core';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../contexts/firebase';
import { bizpotQuestionsHebrew, bizpotQuestionsEnglish } from '../data/bizpot-questions';
import { format } from 'date-fns';

interface QuestionAnswer {
  q: string;
  a: string | 0;
}

interface ParticipantInfo {
  FirstName: string;
  LastName: string;
  CompanyName: string;
  Title: string;
  BusinessType: string;
  EmploeesNumber: string;
  Address: string;
  MobilePhone: string;
}

const emptyParticipant: ParticipantInfo = {
  FirstName: '',
  LastName: '',
  CompanyName: '',
  Title: '',
  BusinessType: '',
  EmploeesNumber: '',
  Address: '',
  MobilePhone: '',
};

export default function BizPotPage() {
  const { t, locale } = useLocale();
  const [participant, setParticipant] = useState<ParticipantInfo>({ ...emptyParticipant });
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [questions, setQuestions] = useState<QuestionAnswer[]>([]);
  const [finished, setFinished] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [textSize, setTextSize] = useState(1);

  const rawQuestions = locale === 'en' ? bizpotQuestionsEnglish : bizpotQuestionsHebrew;
  const unanswered = questions.filter((q) => q.a === 0).length;

  const startQuestionnaire = () => {
    if (!participant.FirstName || !participant.LastName || !participant.Title || !participant.CompanyName) return;
    const qs = rawQuestions.map((q) => ({ q, a: 0 as string | 0 }));
    setQuestions(qs);
    setShowQuestionnaire(true);
  };

  const markAnswer = (idx: number, answer: string) => {
    setQuestions((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], a: answer };
      return next;
    });
  };

  const finishQuestionnaire = () => {
    setFinished(true);
  };

  const submitQuestionnaire = async () => {
    setSubmitted(true);
    await addDoc(collection(db, 'ppatests'), {
      ...participant,
      Questions: questions,
      Date: format(new Date(), 'dd/MM/yyyy'),
      CreatedDate: serverTimestamp(),
      type: 'bizpot',
    });
  };

  return (
    <div className="bp-page" dir="rtl">
      <div className="bp-header">
        <h1>🔑 {t('planning.bizpot.title')}</h1>
        <p className="bp-brand">{t('planning.bizpot.brand')}</p>
        <p className="bp-desc">
          {t('planning.bizpot.description')}
        </p>
        <p><strong>{t('planning.bizpot.important')}</strong></p>
      </div>

      {/* Participant Info Form */}
      {!showQuestionnaire && !finished && (
        <div className="bp-form-card">
          <h3>{t('planning.bizpot.details')}</h3>
          <div className="bp-form-grid">
            <div className="bp-field"><label>* {t('planning.bizpot.firstName')}</label><input className="bp-input" required value={participant.FirstName} onChange={(e) => setParticipant((p) => ({ ...p, FirstName: e.target.value }))} /></div>
            <div className="bp-field"><label>* {t('planning.bizpot.lastName')}</label><input className="bp-input" required value={participant.LastName} onChange={(e) => setParticipant((p) => ({ ...p, LastName: e.target.value }))} /></div>
            <div className="bp-field"><label>* {t('planning.bizpot.title2')}</label><input className="bp-input" required value={participant.Title} onChange={(e) => setParticipant((p) => ({ ...p, Title: e.target.value }))} /></div>
            <div className="bp-field"><label>* {t('planning.bizpot.company')}</label><input className="bp-input" required value={participant.CompanyName} onChange={(e) => setParticipant((p) => ({ ...p, CompanyName: e.target.value }))} /></div>
            <div className="bp-field"><label>* {t('planning.bizpot.businessType')}</label><input className="bp-input" required value={participant.BusinessType} onChange={(e) => setParticipant((p) => ({ ...p, BusinessType: e.target.value }))} /></div>
            <div className="bp-field"><label>{t('planning.bizpot.employeeCount')}</label><input className="bp-input" value={participant.EmploeesNumber} onChange={(e) => setParticipant((p) => ({ ...p, EmploeesNumber: e.target.value }))} /></div>
            <div className="bp-field"><label>{t('planning.bizpot.email')}</label><input className="bp-input" value={participant.Address} onChange={(e) => setParticipant((p) => ({ ...p, Address: e.target.value }))} /></div>
            <div className="bp-field"><label>{t('planning.bizpot.phone')}</label><input className="bp-input" value={participant.MobilePhone} onChange={(e) => setParticipant((p) => ({ ...p, MobilePhone: e.target.value }))} /></div>
          </div>
          <button className="bp-btn bp-btn-success" onClick={startQuestionnaire}>
            {t('planning.bizpot.startAssessment')}
          </button>
        </div>
      )}

      {/* Instructions + Font size */}
      {showQuestionnaire && !finished && (
        <div className="bp-instructions">
          <strong>{t('planning.bizpot.instructions')}</strong>
          <p>
            {t('planning.bizpot.questionCount').replace('{count}', String(rawQuestions.length))}<br />
            {t('planning.bizpot.answerPlus')} | {t('planning.bizpot.answerQuestion')} | {t('planning.bizpot.answerMinus')}
          </p>
          <div className="bp-font-controls">
            {t('planning.bizpot.fontSize')}:
            <button className="bp-btn-sm" onClick={() => setTextSize((s) => s - 0.25)}>−</button>
            <span>{Math.round(textSize * 100)}%</span>
            <button className="bp-btn-sm" onClick={() => setTextSize((s) => s + 0.25)}>+</button>
          </div>
        </div>
      )}

      {/* Questions */}
      {showQuestionnaire && !finished && (
        <>
          <div className="bp-questions" style={{ fontSize: `${textSize}rem` }}>
            {questions.map((q, i) => (
              <div key={i} className="bp-question">
                <span className="bp-q-text">{i + 1}) {q.q}</span>
                <span className="bp-q-answers" dir="ltr">
                  {['+', '?', '-'].map((opt) => (
                    <label key={opt} className={`bp-radio ${q.a === opt ? 'selected' : ''}`}>
                      <input type="radio" name={`q-${i}`} value={opt} checked={q.a === opt} onChange={() => markAnswer(i, opt)} />
                      <span>{opt}</span>
                    </label>
                  ))}
                </span>
              </div>
            ))}
          </div>
          <div className="bp-submit-section">
            <button
              className="bp-btn bp-btn-success"
              disabled={unanswered > 0}
              onClick={finishQuestionnaire}
            >
              {t('planning.bizpot.finish')}
            </button>
            {unanswered > 0 && (
              <p className="bp-warning">* {t('planning.bizpot.unansweredWarning').replace('{count}', String(unanswered))}</p>
            )}
          </div>
        </>
      )}

      {/* Confirmation */}
      {finished && !submitted && (
        <div className="bp-form-card">
          <h3>{t('planning.bizpot.confirmDetails')}</h3>
          <div className="bp-form-grid">
            {Object.entries(participant).map(([key, val]) => (
              <div key={key} className="bp-field">
                <label>{key}</label>
                <input className="bp-input" value={val} readOnly />
              </div>
            ))}
          </div>
          <button className="bp-btn bp-btn-success" onClick={submitQuestionnaire}>{t('planning.bizpot.submit')}</button>
        </div>
      )}

      {submitted && (
        <div className="bp-success">
          <span className="bp-success-badge">✅ {t('planning.bizpot.successMessage')}</span>
        </div>
      )}

      <style>{`
        .bp-page { max-width: 900px; margin: 0 auto; }
        .bp-header { text-align: center; margin-bottom: 2rem; }
        .bp-header h1 { margin: 0; font-size: 1.5rem; }
        .bp-brand { color: var(--text-secondary, #757575); font-size: 0.875rem; margin: 0.25rem 0; }
        .bp-desc { font-size: 0.875rem; max-width: 600px; margin: 0.5rem auto; }

        .bp-form-card { background: var(--surface-color, #fff); border: 1px solid var(--border-color, #e0e0e0); border-radius: var(--radius-lg, 12px); padding: 1.5rem; max-width: 600px; }
        .bp-form-card h3 { margin: 0 0 1rem; }
        .bp-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem; }
        .bp-field { display: flex; flex-direction: column; gap: 0.25rem; }
        .bp-field label { font-size: 0.875rem; font-weight: 500; }
        .bp-input { padding: 0.5rem; border: 1px solid var(--border-color, #e0e0e0); border-radius: var(--radius-md, 8px); font-size: 0.875rem; }

        .bp-btn { padding: 0.75rem 1.5rem; border: none; border-radius: var(--radius-md, 8px); font-size: 1rem; cursor: pointer; }
        .bp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .bp-btn-success { background: #4caf50; color: white; }
        .bp-btn-success:hover:not(:disabled) { background: #43a047; }
        .bp-btn-sm { padding: 0.25rem 0.5rem; border: 1px solid var(--border-color, #e0e0e0); background: var(--surface-color, #fff); border-radius: 4px; cursor: pointer; }

        .bp-instructions { background: #fff8e1; border: 1px solid #ffe082; border-radius: var(--radius-lg, 12px); padding: 1.5rem; margin-bottom: 1rem; }
        .bp-font-controls { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.75rem; }

        .bp-questions { display: grid; grid-template-columns: 1fr 1fr; gap: 0; }
        .bp-question { display: flex; justify-content: space-between; align-items: center; padding: 0.5rem 0.75rem; border: 1px solid #ddd; gap: 0.5rem; }
        .bp-question:hover { background: #f5f5f5; }
        .bp-q-text { flex: 1; }
        .bp-q-answers { display: flex; gap: 0.75rem; }
        .bp-radio { display: flex; align-items: center; gap: 0.25rem; cursor: pointer; }
        .bp-radio input { cursor: pointer; }
        .bp-radio.selected span { font-weight: 700; color: var(--primary-color, #1976d2); }

        .bp-submit-section { text-align: center; padding: 1.5rem; }
        .bp-warning { color: var(--text-secondary, #757575); font-size: 0.875rem; }
        .bp-success { text-align: center; padding: 3rem; }
        .bp-success-badge { background: #4caf50; color: white; padding: 0.75rem 1.5rem; border-radius: var(--radius-md, 8px); font-size: 1.25rem; }

        @media (max-width: 768px) {
          .bp-questions { grid-template-columns: 1fr; }
          .bp-form-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}
